import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { QuestionRenderer } from "@/components/QuestionRenderer";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Heart, Home, MapPin, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const waveConfig = {
  1: {
    title: "Ola 1: ADN Emocional",
    subtitle: "Hablemos de sensaciones...",
    icon: Heart,
    color: "text-red-500",
    bgColor: "bg-red-50"
  },
  2: {
    title: "Ola 2: Arquitectura y Técnica",
    subtitle: "La ingeniería del bienestar",
    icon: Home,
    color: "text-blue-500",
    bgColor: "bg-blue-50"
  },
  3: {
    title: "Ola 3: Entorno y Urbanismo",
    subtitle: "Tu casa no termina en la puerta",
    icon: MapPin,
    color: "text-green-500",
    bgColor: "bg-green-50"
  },
  4: {
    title: "Ola 4: Viabilidad Financiera",
    subtitle: "Hagamos el sueño sostenible",
    icon: DollarSign,
    color: "text-primary",
    bgColor: "bg-primary/10"
  }
};

export const OnboardingPage = () => {
  const [questions, setQuestions] = useState(null);
  const [currentWave, setCurrentWave] = useState(1);
  const [answers, setAnswers] = useState({});
  const [progress, setProgress] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestions();
    fetchProfile();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error("Failed to fetch questions", error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/profile`);
      if (response.data.completed) {
        navigate("/swipe");
      }
      if (response.data.emotional || response.data.technical || response.data.urban || response.data.financial) {
        const savedAnswers = {
          ...response.data.emotional,
          ...response.data.technical,
          ...response.data.urban,
          ...response.data.financial
        };
        setAnswers(savedAnswers);
      }
    } catch (error) {
      console.error("Failed to fetch profile", error);
    }
  };

  const getCategoryForWave = (wave) => {
    const mapping = {
      1: "emotional",
      2: "technical",
      3: "urban",
      4: "financial"
    };
    return mapping[wave];
  };

  const handleAnswer = async (questionId, answer, category) => {
    try {
      await axios.post(`${API}/onboarding/answer`, {
        question_id: questionId,
        answer,
        category
      });
      
      setAnswers(prev => ({ ...prev, [questionId]: answer }));
      
      if (questions) {
        const totalQuestions = Object.values(questions).flat().length;
        const answeredQuestions = Object.keys({ ...answers, [questionId]: answer }).length;
        setProgress((answeredQuestions / totalQuestions) * 100);
      }
      
      toast.success("¡Guardado! ✨");
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleSkip = async (questionId, category) => {
    await handleAnswer(questionId, "skipped", category);
  };

  const completeWave = () => {
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      if (currentWave < 4) {
        setCurrentWave(currentWave + 1);
        const category = getCategoryForWave(currentWave + 1);
        toast.success(`${waveConfig[currentWave + 1].title} desbloqueada! 🎉`);
      }
    }, 2000);
  };

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/onboarding/answer`, {
        question_id: "completed",
        answer: true,
        category: "meta"
      });
      toast.success("¡Tu ADN de Hogar está completo al 100%! Estamos buscando tu match... 🚀");
      setTimeout(() => navigate("/swipe"), 2000);
    } catch (error) {
      toast.error("Error al completar");
    }
  };

  if (!questions) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Sparkles className="w-12 h-12 text-primary animate-pulse mb-4" />
        <p className="text-[#384451]">Preparando tu experiencia...</p>
      </div>
    );
  }

  const categories = ["emotional", "technical", "urban", "financial"];
  const currentCategory = getCategoryForWave(currentWave);
  const currentQuestions = questions[currentCategory]?.filter(q => q.wave === currentWave) || [];
  const waveInfo = waveConfig[currentWave];
  const WaveIcon = waveInfo.icon;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Splash Screen */}
      {progress === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center py-12"
        >
          <Sparkles className="w-20 h-20 text-primary mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-[#384451] mb-4">
            Tu hogar es mucho más que metros cuadrados
          </h1>
          <p className="text-lg text-[#384451]/70 mb-8">
            Tómate 5 minutos para diseñar tu estilo de vida y nosotros encontraremos el lugar que vibre contigo.
          </p>
        </motion.div>
      )}

      <div className="max-w-4xl mx-auto py-8">
        {/* Smart Progress Bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-semibold text-[#384451] flex items-center gap-3">
                <WaveIcon className={`w-8 h-8 ${waveInfo.color}`} />
                {waveInfo.title}
              </h1>
              <p className="text-[#384451]/70 mt-1">{waveInfo.subtitle}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">{Math.round(progress)}%</div>
              <div className="text-xs text-[#384451]/60">Completado</div>
            </div>
          </div>
          
          <Progress value={progress} className="h-3 bg-gray-200" />
          
          {/* Wave Indicators */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map(wave => (
              <div
                key={wave}
                className={`flex-1 h-2 rounded-full transition-all ${
                  wave < currentWave ? 'bg-primary' :
                  wave === currentWave ? 'bg-primary/50' :
                  'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {progress > 0 && progress < 100 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-[#384451]/60 mt-3 text-center"
            >
              ✨ Tus respuestas se guardan automáticamente en tu Bóveda Digital
            </motion.p>
          )}
        </div>

        {/* Questions */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentWave}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {currentQuestions.map((question, idx) => (
              <Card key={question.id} className="modern-card border-0 overflow-hidden">
                <CardHeader className={waveInfo.bgColor}>
                  <CardTitle className="text-lg font-semibold text-[#384451] flex justify-between items-start">
                    <span>
                      {idx + 1}. {question.text}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSkip(question.id, currentCategory)}
                      className="text-xs text-[#384451]/50 hover:text-[#384451]"
                    >
                      Omitir
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <QuestionRenderer
                    question={question}
                    answer={answers[question.id]}
                    onAnswer={handleAnswer}
                    category={currentCategory}
                  />
                </CardContent>
              </Card>
            ))}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {currentWave > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentWave(currentWave - 1)}
                  className="rounded-xl h-12"
                >
                  ← Ola Anterior
                </Button>
              )}
              
              {currentWave < 4 ? (
                <Button
                  onClick={completeWave}
                  className="ml-auto rounded-xl h-12 bg-primary hover:bg-[#b8c100] text-[#384451] font-semibold px-8"
                >
                  Siguiente Ola →
                </Button>
              ) : (
                <Button
                  onClick={completeOnboarding}
                  className="ml-auto rounded-xl h-12 bg-primary hover:bg-[#b8c100] text-[#384451] font-semibold px-8"
                  data-testid="complete-onboarding-btn"
                >
                  ¡Completar mi ADN de Hogar! 🚀
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Celebration Animation */}
        <AnimatePresence>
          {showCelebration && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 1, repeat: 1 }}
                className="bg-white rounded-3xl p-12 text-center"
              >
                <Sparkles className="w-20 h-20 text-primary mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-[#384451] mb-2">
                  ¡Ola {currentWave} Completa!
                </h2>
                <p className="text-[#384451]/70">
                  {waveInfo.title} sincronizado ✓
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
